import type {
  Comment,
  Cue,
  Id,
  NoteColumnDef,
  Scene,
  ScriptColumn,
  ScriptDocument,
} from "@supermanus/document-model";
import {
  cueHasNotes,
  getCueNote,
  getCueSide,
  isCueSideContentEmpty,
  isLockAvailable,
  sceneHasNotes,
} from "@supermanus/document-model";
import { cueContentPlainText } from "./cueRichText";

const COLUMN_LABEL: Record<ScriptColumn, string> = {
  visual: "billedsiden",
  audio: "lydsiden",
};

/** Active cell locks held by someone other than `userId`. */
export function listCueForeignLocks(
  cue: Cue,
  userId: Id,
  at = Date.now(),
): { column: ScriptColumn; name: string }[] {
  const out: { column: ScriptColumn; name: string }[] = [];
  for (const column of ["visual", "audio"] as ScriptColumn[]) {
    const side = getCueSide(cue, column);
    if (isLockAvailable(side, userId, at)) continue;
    out.push({
      column,
      name: side.lockOwnerName?.trim() || "En anden bruger",
    });
  }
  return out;
}

/**
 * Warning block when deleting a row another user is currently editing.
 * Lock TTL is ~30s and is renewed by heartbeat while they stay in the cell.
 */
export function formatCueLockWarningBlock(cue: Cue, userId: Id): string {
  const locks = listCueForeignLocks(cue, userId);
  if (locks.length === 0) return "";
  const names = [...new Set(locks.map((l) => l.name))];
  const who =
    names.length === 1
      ? names[0]!
      : names.length === 2
        ? `${names[0]} og ${names[1]}`
        : `${names.slice(0, -1).join(", ")} og ${names[names.length - 1]}`;
  if (locks.length === 1) {
    return `\n\n${who} redigerer i øjeblikket ${COLUMN_LABEL[locks[0]!.column]}. Vil du alligevel slette rækken?`;
  }
  if (names.length === 1) {
    return `\n\n${who} redigerer i øjeblikket denne række. Vil du alligevel slette den?`;
  }
  return `\n\n${who} redigerer i øjeblikket denne række. Vil du alligevel slette den?`;
}

/** Lock warnings across several cues (multi-delete). */
export function formatCuesLockWarningBlock(cues: Cue[], userId: Id): string {
  const byName = new Map<string, number>();
  for (const cue of cues) {
    const names = new Set(
      listCueForeignLocks(cue, userId).map((l) => l.name),
    );
    for (const name of names) {
      byName.set(name, (byName.get(name) ?? 0) + 1);
    }
  }
  if (byName.size === 0) return "";
  const parts = [...byName.entries()].map(([name, rows]) =>
    rows === 1
      ? `${name} (1 række)`
      : `${name} (${rows} rækker)`,
  );
  return `\n\nFølgende brugere redigerer i øjeblikket en eller flere af rækkerne:\n${parts.map((p) => `• ${p}`).join("\n")}\n\nVil du alligevel slette?`;
}

/** True when a scene has real crew credits (not just empty person rows). */
export function sceneHasCrewContent(scene: Scene): boolean {
  return (scene.crewRoles ?? []).some((role) =>
    (role.people ?? []).some(
      (p) => p.name.trim() || Boolean(p.detail?.trim()),
    ),
  );
}

const NOTES_PREVIEW_MAX = 900;

/** Plain-text preview of all non-empty note columns for a cue. */
export function formatCueNotesPreview(
  cue: Cue,
  noteColumns: NoteColumnDef[] = [],
): string {
  const primaryId = noteColumns[0]?.id ?? null;
  const cols =
    noteColumns.length > 0
      ? noteColumns
      : cue.notes
        ? Object.keys(cue.notes).map((id, i) => ({
            id,
            title: "Noter",
            sortOrder: i,
          }))
        : cue.sourceNote
          ? [{ id: "legacy", title: "Noter", sortOrder: 0 }]
          : [];

  const parts: string[] = [];
  for (const col of cols) {
    const raw =
      col.id === "legacy"
        ? (cue.sourceNote ?? "")
        : getCueNote(cue, col.id, primaryId);
    if (isCueSideContentEmpty(raw)) continue;
    const plain = cueContentPlainText(raw).trim();
    if (!plain) continue;
    const title = col.title.trim() || "Noter";
    parts.push(`${title}:\n${plain}`);
  }

  // Fallback: notes keyed by ids not in noteColumns
  if (parts.length === 0 && cue.notes) {
    const known = new Set(noteColumns.map((c) => c.id));
    for (const [id, value] of Object.entries(cue.notes)) {
      if (known.has(id) || isCueSideContentEmpty(value)) continue;
      const plain = cueContentPlainText(value).trim();
      if (plain) parts.push(`Noter:\n${plain}`);
    }
  }
  if (parts.length === 0 && cue.sourceNote && !isCueSideContentEmpty(cue.sourceNote)) {
    const plain = cueContentPlainText(cue.sourceNote).trim();
    if (plain) parts.push(`Noter:\n${plain}`);
  }

  let preview = parts.join("\n\n");
  if (preview.length > NOTES_PREVIEW_MAX) {
    preview = `${preview.slice(0, NOTES_PREVIEW_MAX)}…`;
  }
  return preview;
}

/** Cross-view data that would be lost if this cue row is deleted. */
export function listCueLinkedWarnings(
  cue: Cue,
  comments: Comment[] = [],
  noteColumns: NoteColumnDef[] = [],
): string[] {
  const warnings: string[] = [];
  if (cueHasNotes(cue)) {
    const notesPreview = formatCueNotesPreview(cue, noteColumns);
    warnings.push(notesPreview ? `Noter\n${notesPreview}` : "Noter");
  }
  const openComments = comments.filter(
    (c) => c.cueId === cue.id && !c.resolved,
  );
  if (openComments.length === 1) {
    warnings.push("1 kommentar");
  } else if (openComments.length > 1) {
    warnings.push(`${openComments.length} kommentarer`);
  }
  return warnings;
}

/** Cross-view / related data that would be lost if this scene is deleted. */
export function listSceneLinkedWarnings(
  doc: ScriptDocument,
  sceneId: Id,
): string[] {
  const scene = doc.scenes[sceneId];
  if (!scene) return [];
  const warnings: string[] = [];

  if (sceneHasCrewContent(scene)) {
    warnings.push("Crew");
  }
  if ((scene.statusCompletedIds ?? []).length > 0) {
    warnings.push("Status");
  }
  if (scene.description?.trim()) {
    warnings.push("Runddown (beskrivelse)");
  }
  if (sceneHasNotes(scene)) {
    warnings.push("Scenenoter");
  }

  let sourceRows = 0;
  let commentCount = 0;
  let manusRows = 0;
  for (const cueId of scene.cueIds) {
    const cue = doc.cues[cueId];
    if (!cue) continue;
    if (cueHasNotes(cue)) sourceRows += 1;
    commentCount += Object.values(doc.comments ?? {}).filter(
      (c) => c.cueId === cueId && !c.resolved,
    ).length;
    const hasManus =
      !isCueSideContentEmpty(cue.visual.content) ||
      !isCueSideContentEmpty(cue.audio.content) ||
      Boolean(cue.visual.characterName?.trim()) ||
      Boolean(cue.audio.characterName?.trim());
    if (hasManus) manusRows += 1;
  }

  if (manusRows > 0) {
    warnings.push(
      manusRows === 1
        ? "Manus (1 række med tekst)"
        : `Manus (${manusRows} rækker med tekst)`,
    );
  }
  if (sourceRows > 0) {
    warnings.push(
      sourceRows === 1
        ? "Noter (1 række)"
        : `Noter (${sourceRows} rækker)`,
    );
  }
  if (commentCount === 1) {
    warnings.push("1 kommentar");
  } else if (commentCount > 1) {
    warnings.push(`${commentCount} kommentarer`);
  }

  return warnings;
}

export function formatLinkedWarningsBlock(warnings: string[]): string {
  if (warnings.length === 0) return "";
  const notesHint = warnings.some((w) => w.startsWith("Noter"))
    ? "\n\nNoterne slettes kun, fordi du sletter hele rækken — ikke fordi manusteksten ændres eller slettes."
    : "";
  return (
    "\n\nDette sletter også:\n" +
    warnings.map((w) => `• ${w}`).join("\n") +
    notesHint
  );
}

/** Summarize linked warnings across several cues (for multi-delete). */
export function listCuesLinkedWarningsSummary(
  cues: Cue[],
  comments: Comment[] = [],
  noteColumns: NoteColumnDef[] = [],
): string[] {
  const withNotes = cues.filter((c) => cueHasNotes(c));
  let commentCount = 0;
  for (const cue of cues) {
    commentCount += comments.filter(
      (c) => c.cueId === cue.id && !c.resolved,
    ).length;
  }
  const warnings: string[] = [];
  if (withNotes.length === 1) {
    const preview = formatCueNotesPreview(withNotes[0]!, noteColumns);
    warnings.push(preview ? `Noter\n${preview}` : "Noter (1 række)");
  } else if (withNotes.length > 1) {
    const snippets = withNotes
      .slice(0, 3)
      .map((c) => {
        const p = formatCueNotesPreview(c, noteColumns);
        return p ? p.split("\n").slice(0, 2).join(" — ") : "…";
      });
    const more =
      withNotes.length > 3 ? `\n…og ${withNotes.length - 3} rækker mere` : "";
    warnings.push(
      `Noter (${withNotes.length} rækker)\n${snippets.join("\n")}${more}`,
    );
  }
  if (commentCount === 1) {
    warnings.push("1 kommentar");
  } else if (commentCount > 1) {
    warnings.push(`${commentCount} kommentarer`);
  }
  return warnings;
}

export function buildCueDeleteConfirmMessage(opts: {
  emptyManuscript: boolean;
  preview?: string;
  linkedWarnings: string[];
  /** Pre-formatted lock warning (from formatCueLockWarningBlock). */
  lockWarning?: string;
}): string {
  const linked = formatLinkedWarningsBlock(opts.linkedWarnings);
  const lock = opts.lockWarning ?? "";
  if (opts.emptyManuscript) {
    if (opts.linkedWarnings.length > 0) {
      return `Slet denne tomme række i manus?${linked}${lock}`;
    }
    return `Slet denne tomme række?${lock}`;
  }
  return `Vil du slette hele rækken og dens manustekst?\n\n${opts.preview ?? ""}${linked}${lock}`;
}

export function buildSceneDeleteConfirmMessage(opts: {
  sceneNumber: string | number;
  sceneTitle: string;
  linkedWarnings: string[];
  retentionDays?: number;
}): string {
  const title = opts.sceneTitle.trim() || "Unavngiven scene";
  const linked = formatLinkedWarningsBlock(opts.linkedWarnings);
  return `Flyt scene ${opts.sceneNumber} «${title}» til papirkurven? Du kan gendanne den inden for ${opts.retentionDays ?? 30} dage — derefter slettes den automatisk.${linked}`;
}
